import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Users, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

function EventCard({ event, featured, index, past }) {
  const tiers = event.variations || [];
  const anyPaidTier = tiers.some((t) => Number(t.price) > 0);
  const minTier = tiers.length ? Math.min(...tiers.map((t) => Number(t.price))) : null;
  const isFree = Number(event.price) <= 0 && !anyPaidTier;
  const soldOut = !past && event.sold_out;
  const badge = tiers.length
    ? (anyPaidTier ? (minTier <= 0 ? "Free & paid options" : `From $${minTier}`) : "Free")
    : (isFree ? "Free" : `$${Number(event.price).toFixed(0)}`);
  const spotsLabel = !past && event.show_spots
    ? (soldOut ? "Sold out" : `${event.spots_remaining} of ${event.capacity} spots left`)
    : `${event.capacity} spots`;
  return (
    <motion.article
      data-testid={`event-card-${event.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: EASE }}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-500 ${
        past
          ? "border-gold/10 bg-ink-card/50 opacity-75"
          : `hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-20px_rgba(212,175,55,0.25)] ${
              featured ? "border-gold/50 bg-ink-card lg:col-span-2 lg:flex-row" : "border-gold/15 bg-ink-card/70"
            }`
      }`}
    >
      <Link to={`/events/${event.id}`} className={`relative block overflow-hidden ${featured && !past ? "lg:w-1/2" : ""}`}>
        <img
          src={event.image || "/assets/academy-1.png"}
          alt={event.title}
          className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${past ? "grayscale" : ""} ${featured && !past ? "h-64 lg:h-full" : "h-52"}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
        <span data-testid={`event-badge-${event.id}`} className={`absolute left-4 top-4 rounded-full px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] ${
          past ? "bg-zinc-600/90 text-white" : soldOut ? "bg-red-500/90 text-white" : isFree ? "bg-emerald-400/90 text-ink" : "bg-gold text-ink"
        }`}>
          {past ? "Past Event" : soldOut ? "Sold Out" : badge}
        </span>
      </Link>
      <div className={`flex flex-1 flex-col p-6 sm:p-8 ${featured ? "lg:justify-center" : ""}`}>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{event.category}</span>
        <Link to={`/events/${event.id}`} className="transition-colors hover:text-gold">
          <h3 className={`mt-2 font-display font-semibold ${featured ? "text-2xl sm:text-3xl" : "text-xl"}`}>{event.title}</h3>
        </Link>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{event.description}</p>
        <div className="mt-5 space-y-2 text-sm text-foreground/75">
          <div className="flex items-center gap-2.5"><CalendarDays className="h-4 w-4 text-gold" /> {event.date_label}</div>
          <div className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-gold" /> {event.location}</div>
          <div className="flex items-center gap-2.5"><Users className="h-4 w-4 text-gold" /> <span data-testid={`event-spots-${event.id}`}>{spotsLabel}</span></div>
        </div>
        <Button
          data-testid={`event-more-info-button-${event.id}`}
          asChild
          variant={past ? "outline" : "default"}
          className={past
            ? "mt-6 w-fit rounded-full border-gold/30 bg-transparent px-6 font-semibold text-gold hover:bg-gold/10"
            : "mt-6 w-fit rounded-full bg-gold px-6 font-semibold text-ink transition-all duration-300 hover:scale-[1.03] hover:bg-gold-hover"}
        >
          <Link to={`/events/${event.id}`}>{past ? "View Details" : "More Info"} <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>
    </motion.article>
  );
}

export default function EventsSection() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events")
      .then(({ data }) => setEvents(data))
      .catch(() => toast.error("Could not load events."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="events" data-testid="events-section" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-14 flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Upcoming Events</span>
          <h2 className="mt-4 max-w-xl font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
            Where leaders are made. Reserve your seat.
          </h2>
        </div>
        <p className="max-w-sm text-sm text-muted-foreground">
          From the Elite Mentorship Academy to free empowerment sessions — every gathering is built to shift atmospheres.
        </p>
      </motion.div>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="events-loading"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
      ) : (
        (() => {
          const upcoming = events.filter((e) => !e.past);
          const past = events.filter((e) => e.past);
          return (
            <>
              {upcoming.length === 0 ? (
                <p className="py-16 text-center text-muted-foreground" data-testid="events-empty">New events are on the way — check back soon.</p>
              ) : (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="upcoming-events-grid">
                  {upcoming.map((e, i) => (
                    <EventCard key={e.id} event={e} featured={e.featured} index={i} past={false} />
                  ))}
                </div>
              )}
              {past.length > 0 && (
                <div className="mt-20" data-testid="past-events-section">
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground" data-testid="past-events-heading">Past Events</p>
                  <p className="mt-2 max-w-md text-sm text-muted-foreground">Browse highlights from previous gatherings — details only, registration is closed.</p>
                  <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {past.map((e, i) => (
                      <EventCard key={e.id} event={e} featured={false} index={i} past />
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()
      )}
    </section>
  );
}
